import { useEffect, useMemo, useState } from 'react';
import databaseMonitorService, { ServerInfo } from '../../../../../services/databaseMonitor.service';
import { Input } from '../ui';

const KEY_STATUS = [
  'Uptime',
  'Threads_connected',
  'Threads_running',
  'Queries',
  'Slow_queries',
  'Aborted_clients',
  'Aborted_connects',
  'Max_used_connections',
  'Innodb_buffer_pool_pages_free',
  'Innodb_buffer_pool_pages_total',
];

export function ServerInfoView({ serverId }: { serverId: string }) {
  const [data, setData] = useState<ServerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setData(null);
    databaseMonitorService
      .getServerInfo(serverId, controller.signal)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((err: any) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load server info');
        setLoading(false);
      });
    return () => controller.abort();
  }, [serverId]);

  const keyStatus = useMemo(() => {
    if (!data) return [] as (readonly [string, string])[];
    return KEY_STATUS.filter((k) => k in data.status).map((k) => [k, data.status[k]] as const);
  }, [data]);

  const filteredVars = useMemo(() => {
    if (!data) return [] as [string, string][];
    const f = filter.toLowerCase();
    return Object.entries(data.variables)
      .filter(([k]) => !f || k.toLowerCase().includes(f))
      .sort(([a], [b]) => a.localeCompare(b));
  }, [data, filter]);

  if (loading) return <div className="text-slate-600 dark:text-slate-400">Loading…</div>;
  if (error) return <div className="text-red-600 dark:text-red-400">Error: {error}</div>;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Version" value={data.version ?? '—'} />
          <Stat label="Uptime" value={formatUptime(data.uptime_seconds)} />
          {keyStatus.map(([k, v]) => (
            <Stat key={k} label={k} value={v} />
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Variables</h2>
          <Input
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <div className="border border-slate-200 dark:border-slate-800 rounded-md max-h-96 overflow-auto">
          <table className="min-w-full text-xs">
            <tbody>
              {filteredVars.map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100 dark:border-slate-900">
                  <td className="px-3 py-1 text-slate-600 dark:text-slate-400 whitespace-nowrap">{k}</td>
                  <td className="px-3 py-1 break-all text-slate-900 dark:text-slate-100">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2">
      <div className="text-xs text-slate-600 dark:text-slate-400">{label}</div>
      <div className="text-sm truncate text-slate-900 dark:text-slate-100" title={value}>
        {value}
      </div>
    </div>
  );
}

function formatUptime(seconds: number | null): string {
  if (seconds == null) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
}

export default ServerInfoView;
